import ctl from '@netlify/classnames-template-literals'
import { ValidationError } from '@tanstack/react-form'
import { Fragment, ReactNode, useCallback, useState } from 'react'
import { useDisplayDate, useMonthView } from '../calendar'
import { GenericField, GenericInput, GenericPopupInput } from './generic'

export interface DateFieldProps {
	label?: string
	value: Date
	onChange: (value: Date) => void
	onBlur?: () => void
	hint?: ReactNode
	errors?: Array<ValidationError>
	minDate?: Date
	maxDate?: Date
	hideLabel?: boolean
}

export function DateField(
	{ label, value, onChange, onBlur, hint, errors, minDate, maxDate, hideLabel }: DateFieldProps,
) {
	const [month, setMonth] = useState((new Date()).getMonth())
	const [year, setYear] = useState((new Date()).getFullYear())

	const monthNames = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	]

	const calendarDays = useMonthView(month, year, value)
	const displayDate = useDisplayDate()

	const handleMonthChange = (delta: number) => {
		const newMonth = month + delta
		if (newMonth > 11) {
			setMonth(0)
			setYear(year + 1)
		} else if (newMonth < 0) {
			setMonth(11)
			setYear(year - 1)
		} else {
			setMonth(newMonth)
		}
	}

	const isDateDisabled = useCallback(
		(date: Date) => {
			const selectedDate = new Date(date)
			selectedDate.setHours(0, 0, 0, 0)

			if (minDate) {
				const min = new Date(minDate)
				min.setHours(0, 0, 0, 0)
				if (selectedDate < min) {
					return true
				}
			}

			if (maxDate) {
				const max = new Date(maxDate)
				max.setHours(0, 0, 0, 0)
				if (selectedDate > max) {
					return true
				}
			}

			return false
		},
		[minDate, maxDate],
	)

	return (
		<GenericField label={label} errors={errors} hint={hint} hideLabel={hideLabel}>
			<GenericPopupInput
				triggerWrapperClassName="w-full"
				trigger={
					<GenericInput
						isGenerating={false}
						isLoading={false}
						isDisabled={false}
					>
						{({ className }) => (
							<div className={`${className} w-full text-base font-normal`}>{displayDate(value)}</div>
						)}
					</GenericInput>
				}
			>
				<div className="">
					<div className="flex items-center gap-2 pt-4 mb-8 px-3">
						<button
							onClick={() => handleMonthChange(-1)}
							className="text-grey-600 cursor-pointer p-3 hover:text-grey-900"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 16 16"
								fill="currentColor"
								className="size-4"
							>
								<path
									fillRule="evenodd"
									d="M14 8a.75.75 0 0 1-.75.75H4.56l3.22 3.22a.75.75 0 1 1-1.06 1.06l-4.5-4.5a.75.75 0 0 1 0-1.06l4.5-4.5a.75.75 0 0 1 1.06 1.06L4.56 7.25h8.69A.75.75 0 0 1 14 8Z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
						<div className="flex-1" />
						<div>{monthNames[month]}</div>
						<div>{year}</div>
						<div className="flex-1" />
						<button
							onClick={() => handleMonthChange(1)}
							className="text-grey-600 cursor-pointer p-3 hover:text-grey-900"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 16 16"
								fill="currentColor"
								className="size-4"
							>
								<path
									fillRule="evenodd"
									d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					</div>
					<div className="grid grid-cols-7 gap-0">
						{['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, i) => (
							<div
								key={i}
								className="text-xs text-grey-800 pb-4 font-medium text-center border-b border-grey-100"
							>
								{day}
							</div>
						))}
						{calendarDays.map((week, weekIndex) => (
							<Fragment key={weekIndex}>
								{week.map((day, dayIndex) => {
									const isDisabled = isDateDisabled(day.jsDate)
									return (
										<button
											key={dayIndex}
											disabled={isDisabled}
											className={ctl(`
                                            text-sm
                                            cursor-pointer
                                            flex items-center justify-center rounded
                                            hover:bg-grey-50 aspect-square
                                            ${day.isSelected ? '!bg-brand-500 !text-white font-bold' : ''}
                                            ${!day.isCurrentMonth ? 'opacity-50' : ''}
                                            ${day.isToday ? 'underline decoration-2' : ''}
                                            ${day.isWeekend ? 'text-red-500' : ''}
                                            ${isDisabled ? 'disabled:opacity-40 disabled:cursor-not-allowed' : ''}
                                        `)}
											onClick={() => onChange(day.jsDate)}
										>
											<div className="p-3">{day.date}</div>
										</button>
									)
								})}
							</Fragment>
						))}
					</div>
				</div>
			</GenericPopupInput>
		</GenericField>
	)
}
